import { FormGroup, H5 } from '@blueprintjs/core';
import * as React from 'react';
import { SelectItem } from 'vet-bones/utils/select';

import { PromptDialog } from 'src/components/PromptDialog';
import { ProjectResourceType } from 'src/constants/project';
import { CreateMappingDialogClassSelector } from 'src/containers/ProjectDiagram/components/dialogs/CreateMappingDialogClassSelector';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import {
  DiagramModelCommitLayout,
  commitDiagramModel,
} from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import { addMapping } from 'src/containers/ProjectDiagram/utils/mapping/addMapping';
import { getModelConceptClassKey } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';
import { useCommitOneResourceOneClassCallback } from 'src/containers/ProjectResourceSuggestionsDialog/hooks/useCommitOneResourceOneClassCallback';
import { connectionSelector } from 'src/selectors/connection';
import { isStardogFeatureSupportedSelector } from 'src/selectors/isStardogFeatureSupported';
import { projectClassItemsSelector } from 'src/selectors/projectClassItems';
import { projectResourceSelector } from 'src/selectors/projectResource';
import { ProjectVirtualGraph } from 'src/store/state/projects';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { usePortalConnection } from 'src/utils/hooks/usePortalConnection';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

export const CreateMappingDialog: React.VFC = () => {
  const { projectId } = useRouterParams();
  const dispatch = useReduxDispatch();
  const diagram = useDiagram();
  const connection = useReduxSelector(connectionSelector);

  const isAutoMappingSupported = useReduxSelector(
    isStardogFeatureSupportedSelector('isAutoMappingSupported', connection)
  );

  const commitOneResourceOneClass = useCommitOneResourceOneClassCallback();

  const { initialValue, isOpen, fromId, fields, toValue } = useReduxSelector(
    (state) => state.ui.createMappingDialog
  );

  const maybeVirtualGraph = useReduxSelector(
    projectResourceSelector(
      projectId,
      fromId,
      ProjectResourceType.VIRTUAL_GRAPH
    )
  ) as ProjectVirtualGraph | null;

  const classItems = useReduxSelector(projectClassItemsSelector(projectId));

  usePortalConnection({
    connectionId: maybeVirtualGraph?.connectionId,
    enabled: isOpen,
    key: toValue,
  });

  const defaultLabel =
    (diagram &&
      copy.components.projectDiagram.mapping.defaultLabel(
        diagram.model.modelData.mappingIdx
      )) ||
    '';

  const selectedClassItem = React.useMemo(() => {
    return classItems.find((item) => item.value === toValue) || null;
  }, [classItems, toValue]);

  const handleClose = React.useCallback(() => {
    dispatch(
      uiSlice.actions.toggledCreateMappingDialog({
        isOpen: false,
      })
    );
  }, [dispatch]);

  const errorTextForLabel = React.useCallback((label: string) => {
    return label
      ? ''
      : copy.components.projectDiagram.createDialogs.errors.noName;
  }, []);

  const handleAccept = React.useCallback(
    (text: string) => {
      if (!fromId || !fields || !diagram) {
        return;
      }

      const toKey = toValue ? getModelConceptClassKey(toValue) : undefined;

      if (isAutoMappingSupported && toValue) {
        commitOneResourceOneClass({
          mappingName: text,
          selectedClasses: [toValue],
          selectedResources: [fromId],
        });
      } else {
        commitDiagramModel(
          diagram,
          addMapping({ fromId, fields, text, toKey }),
          { layout: DiagramModelCommitLayout.PARTIAL }
        );
      }

      handleClose();
    },
    [
      fromId,
      fields,
      diagram,
      toValue,
      isAutoMappingSupported,
      handleClose,
      commitOneResourceOneClass,
    ]
  );
  const handleSelectClass = React.useCallback(
    (item: SelectItem | null) => {
      dispatch(
        uiSlice.actions.selectedMappingDialogClass({
          toValue: item?.value,
        })
      );
    },
    [dispatch]
  );

  const classInput = (
    <FormGroup
      label={<H5>{copy.components.projectDiagram.mapping.mappingTarget}</H5>}
      subLabel={copy.components.projectDiagram.mapping.mappingTargetHelp}
    >
      <CreateMappingDialogClassSelector
        classItems={classItems}
        handleSelectClass={handleSelectClass}
        selectedClassItem={selectedClassItem}
      />
    </FormGroup>
  );

  return (
    <PromptDialog
      additionalInputs={classInput}
      getErrorTextForValue={errorTextForLabel}
      handleCancel={handleClose}
      handleSubmit={handleAccept}
      initialValue={initialValue || defaultLabel}
      isOpen={isOpen}
      message={<H5>{copy.components.projectDiagram.mapping.createMessage}</H5>}
      submitButtonText={
        copy.components.projectDiagram.createDialogs.createButton
      }
      title={copy.components.projectDiagram.mapping.new}
    />
  );
};
